import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  buildDql, createExtractorsApi, deletionTargets, isCustom, isDraftId, isEnabled, nativeOf,
  needsAttention, parseValidationDetail, summariseValidation, updatedAtMs,
} from '@bloo-team/api-client'
import type { Extractor, ExtractorsPage, TopExtractor, ValidationDetail } from '@bloo-team/api-client'
import { client, getSession, hasRole, identityFor, isDemoSession, runToCompletion, searchApiFor } from '@bloo-team/core-shell'
import { extractorOverlayStore, useExtractorOverlay, type OverlayMap } from './extractor-store'

/**
 * Extractors data — POST /{clusterId}/api/parsers/list.
 *
 * The whole set is fetched in one request (217 in the capture, well inside the
 * honoured pagesize), so filtering, counting and paging are client-side and the
 * chip counts are accurate over everything rather than one page.
 */

export const extractorsApi = createExtractorsApi(client)

export {
  deletionTargets, isCustom, isDraftId, isEnabled, nativeOf, needsAttention,
  parseValidationDetail, summariseValidation, updatedAtMs,
}
export type { Extractor, TopExtractor, ValidationDetail }

/** Fetched in one go; the endpoint honoured this and returned all 217. */
const ALL_PAGE_SIZE = 1000

const mk = (
  name: string, source_name: string, source_type: string, status: string,
  extractor_type: string, stage: string | null, msg: string | null, id: string, ageDays: number,
): Extractor => ({
  extractor_id: id,
  entity_id: `e-${id}`,
  name,
  status,
  publish_status: 'published',
  extractor_type,
  source_name,
  source_type,
  author: 'bloo',
  version: '1.0',
  validation_stage: stage,
  validation_msg: msg,
  update_tstamp: String(Math.floor(Date.now() / 1000) - ageDays * 86_400),
})

/**
 * Exported so tests can assert these validate against the live-captured schema.
 *
 * Ids follow the live identity model (native = bare number, custom twin =
 * `<native>_<scope>`) AND agree with the other demo fixtures: the sources
 * fixture binds devices to 4001…4005, and the platform search fixtures stamp
 * `ExtractorID` 4001 (FIREWALL), 4002 (WIN-AUDIT), 4004 (EP-PROCESS) and 4005
 * on the OTHER fallout — so the health board's volume join works in demo.
 */
export const DEMO_EXTRACTORS: Extractor[] = [
  mk('accops-accops_hysecure-syslog', 'ACCOPS-HYSECURE', 'ZERO-TRUST', 'ENABLED', 'native', 'Integrator Success', 'Success', '124', 1),
  mk('fortinet-fortigate-syslog_kv', 'FORTIGATE', 'FIREWALL', 'ENABLED', 'native', 'Integrator Success', 'Success', '4001', 2),
  mk('ms-windows-winlogbeat', 'WINDOWS', 'ENDPOINT-SECURITY', 'ENABLED', 'native', 'Integrator Success', 'Success', '4002', 3),
  mk('nginx-access', 'NGINX', 'APPLICATION', 'DISABLED', 'native', 'Integrator Success', 'Success', '4005', 4),
  // The cases that need a human — each exercises a different validation shape.
  mk('AI-GEN-TEST', 'CUSTOM-APP', 'APPLICATION', 'DISABLED', 'custom', 'New Field Found',
    JSON.stringify([
      { stream: 'FIREWALL', stream_type: 'existing', new_fields: ['Hostname', 'UserAgent', 'URI'] },
      { stream: 'WEBFILTER', stream_type: 'existing', new_fields: ['Hostname'] },
    ]), '10002_gotham', 5),
  mk('test-ext', 'SOPHOS-UTM', 'FIREWALL', 'DISABLED', 'custom', 'Integrator Failed',
    JSON.stringify([{ 'extractor-match-list': [
      { 'extractor-id': 820, 'extractor-name': 'sophos-sophos_utm-syslog_kv' },
      { 'extractor-id': 820, 'extractor-name': 'sophos-sophos_utm-syslog_kv' },
    ] }]), '820_gotham', 6),
  mk('MS-O365-New-Log-Parser', 'MS-O365', 'CLOUD', 'DISABLED', 'custom', 'Service Request raised',
    JSON.stringify([{ stream: 'COMPLIANCE', stream_type: 'existing', new_fields: ['Operation', 'URL'] }]), '231_gotham', 7),
  // Null stage and null message both occur in the real data.
  mk('darktrace-dcip-syslog_cef-custom', 'DARKTRACE', 'THREAT', 'ENABLED', 'custom', null, null, '4004_gotham', 8),
]
// AI-GEN-TEST is a DRAFT on the live tenant (publish_status "draft"); mk() defaults to published.
DEMO_EXTRACTORS[4]!.publish_status = 'draft'

/**
 * Demo writes land in the overlay store (docs/extractors.md §Demo store) and
 * are applied over the fixtures here, so the page has ONE code path.
 */
export function applyOverlay(rows: Extractor[], overlay: OverlayMap): Extractor[] {
  const known = new Set(rows.map((e) => e.extractor_id))
  const created = Object.values(overlay)
    .map((o) => o.created)
    .filter((c): c is Extractor => !!c && !known.has(c.extractor_id))
  return [...rows, ...created]
    .filter((e) => !overlay[e.extractor_id]?.deleted)
    .map((e) => {
      const o = overlay[e.extractor_id]
      if (!o?.status) return e
      return { ...e, status: o.status, update_tstamp: String(Math.floor(o.at / 1000)) }
    })
}

const listKey = (clusterId: string, scopeId: string) =>
  ['extractors', clusterId, scopeId, isDemoSession() ? 'demo' : 'live'] as const

export function useExtractors({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const overlay = useExtractorOverlay(clusterId, scopeId)
  const q = useQuery<ExtractorsPage>({
    queryKey: listKey(clusterId, scopeId),
    queryFn: async () => {
      if (isDemo) return { extractors: DEMO_EXTRACTORS, total: DEMO_EXTRACTORS.length }
      return extractorsApi.list({ clusterId, scopeId, pageNo: 1, pageSize: ALL_PAGE_SIZE })
    },
    retry: 1,
  })
  const data = useMemo<ExtractorsPage | undefined>(() => {
    if (!q.data || !isDemo) return q.data
    const extractors = applyOverlay(q.data.extractors, overlay)
    return { extractors, total: extractors.length }
  }, [q.data, isDemo, overlay])
  return { ...q, data }
}

/* ---------------------------------------------------------------- writes */

/**
 * Who may write. Legacy gates the whole extraction screen on Security
 * Engineer; the console shows the read surface to analysts and keeps the
 * write controls engineer-only. A UX affordance — authorization is the
 * server's (permissions.ts).
 */
export function canWriteExtractors(clusterId: string, scopeId: string): boolean {
  const session = getSession()
  if (!session) return false
  return hasRole(identityFor(session, clusterId, scopeId), ['Security Engineer'])
}

function useInvalidate(clusterId: string, scopeId: string) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['extractors', clusterId, scopeId] })
}

/**
 * Enable/disable. Asymmetric on the wire (captured 2026-09-08, CB-24):
 * disable is `changestatus`; enable is `save` with call_type STATE, which
 * re-validates and re-publishes — so it can take a moment and can fail on a
 * draft. Not optimistic: the list re-reads after the backend answers.
 */
export function useSetExtractorStatus(scope: { clusterId: string; scopeId: string }) {
  const invalidate = useInvalidate(scope.clusterId, scope.scopeId)
  return useMutation<void, Error, { extractor: Extractor; enabled: boolean }>({
    mutationFn: async ({ extractor, enabled }) => {
      if (isDemoSession()) {
        extractorOverlayStore(scope.clusterId, scope.scopeId).setStatus(extractor.extractor_id, enabled ? 'ENABLED' : 'DISABLED')
        return
      }
      if (enabled) await extractorsApi.enable({ ...scope, extractorId: extractor.extractor_id, name: extractor.name })
      else await extractorsApi.disable({ ...scope, extractorId: extractor.extractor_id })
    },
    onSuccess: invalidate,
  })
}

/**
 * Delete a custom extractor. `parsers/delete` removes ONLY the id it is given
 * and a clone leaves a published twin AND a draft (CB-25), so this deletes
 * every row `deletionTargets` names — one call each, in order, stopping at
 * the first failure so a half-done delete is visible rather than hidden.
 */
export function useDeleteExtractor(scope: { clusterId: string; scopeId: string }) {
  const invalidate = useInvalidate(scope.clusterId, scope.scopeId)
  return useMutation<string[], Error, { targets: Extractor[] }>({
    mutationFn: async ({ targets }) => {
      const ids = targets.map((t) => t.extractor_id)
      if (isDemoSession()) {
        extractorOverlayStore(scope.clusterId, scope.scopeId).markDeleted(ids)
        return ids
      }
      for (const t of targets) {
        await extractorsApi.remove({ ...scope, extractorId: t.extractor_id, extractorType: t.extractor_type || 'custom' })
      }
      return ids
    },
    onSuccess: invalidate,
  })
}

/* ------------------------------------------------------------------- top */

/** Demo twin of `parsers/top` — the ranks the WAYNE-CORP home showed. */
export const DEMO_TOP_EXTRACTORS: TopExtractor[] = [
  { rank: 1, extractorId: '4002', name: 'ms-windows-winlogbeat', events: 268_105 },
  { rank: 2, extractorId: '4001', name: 'fortinet-fortigate-syslog_kv', events: 95_991 },
  { rank: 3, extractorId: '124', name: 'accops-accops_hysecure-syslog', events: 55_186 },
  { rank: 4, extractorId: '4004_gotham', name: 'darktrace-dcip-syslog_cef-custom', events: 4_674 },
  { rank: 5, extractorId: '231_gotham', name: 'MS-O365-New-Log-Parser', events: 4_630 },
]

export function useTopExtractors({ clusterId, scopeId, duration = '24h' }: { clusterId: string; scopeId: string; duration?: string }) {
  const isDemo = isDemoSession()
  return useQuery<TopExtractor[]>({
    queryKey: ['extractors-top', clusterId, scopeId, duration, isDemo ? 'demo' : 'live'],
    queryFn: async () => {
      if (isDemo) return DEMO_TOP_EXTRACTORS
      return extractorsApi.top({ clusterId, scopeId, duration })
    },
    staleTime: 60_000,
    retry: 1,
  })
}

/** Compact event count for the widget: 268105 → "268k", 4674 → "4.7k". */
export function compactCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}k`
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

/* ------------------------------------------------------------------ display */

export type ExtractorFilter =
  | 'all' | 'enabled' | 'disabled' | 'custom' | 'attention'
  /** Health chips (requirements W1) — meaningful only with volumes loaded. */
  | 'silent' | 'fallout' | 'draft'

export function isDraft(e: Extractor): boolean {
  return e.publish_status.toLowerCase() === 'draft'
}

/**
 * The health predicates need the volume join; without it (live queries
 * failed, or UNTESTED-blocked) `silent` and `fallout` match nothing and the
 * page hides those chips rather than show zeros.
 */
export function matchesFilter(e: Extractor, filter: ExtractorFilter, volumes?: VolumeMap | null): boolean {
  switch (filter) {
    case 'all': return true
    case 'enabled': return isEnabled(e)
    case 'disabled': return !isEnabled(e)
    case 'custom': return isCustom(e)
    case 'attention': return needsAttention(e)
    case 'draft': return isDraft(e)
    case 'silent': return !!volumes && isEnabled(e) && volumeOf(e, volumes).parsed === 0
    case 'fallout': return !!volumes && volumeOf(e, volumes).fallout > 0
  }
}

export function countsFor(extractors: Extractor[], volumes?: VolumeMap | null): Record<ExtractorFilter, number> {
  const n = (f: ExtractorFilter) => extractors.filter((e) => matchesFilter(e, f, volumes)).length
  return {
    all: extractors.length,
    enabled: n('enabled'),
    disabled: n('disabled'),
    custom: n('custom'),
    attention: n('attention'),
    draft: n('draft'),
    silent: n('silent'),
    fallout: n('fallout'),
  }
}

export type ExtractorSortKey = 'name' | 'source' | 'type' | 'status' | 'stage' | 'updated' | 'volume'
export type SortDir = 'asc' | 'desc'

export function sortExtractors(
  rows: Extractor[], key: ExtractorSortKey, dir: SortDir, volumes?: VolumeMap | null,
): Extractor[] {
  const text = (e: Extractor): string => {
    switch (key) {
      case 'name': return e.name
      case 'source': return sourceLabel(e)
      case 'type': return e.extractor_type
      case 'status': return e.status
      case 'stage': return e.validation_stage ?? ''
      default: return ''
    }
  }
  return [...rows].sort((a, b) => {
    if (key === 'volume') {
      const av = volumes ? volumeOf(a, volumes).parsed : 0
      const bv = volumes ? volumeOf(b, volumes).parsed : 0
      return (dir === 'asc' ? av - bv : bv - av) || a.name.localeCompare(b.name)
    }
    if (key === 'updated') {
      const av = updatedAtMs(a)
      const bv = updatedAtMs(b)
      // Unknown timestamps sort last whichever way the arrow points.
      if (av === null && bv === null) return a.name.localeCompare(b.name)
      if (av === null) return 1
      if (bv === null) return -1
      return (dir === 'asc' ? av - bv : bv - av) || a.name.localeCompare(b.name)
    }
    // Empty strings sort last, for the same reason.
    const as = text(a)
    const bs = text(b)
    if (!as && !bs) return a.name.localeCompare(b.name)
    if (!as) return 1
    if (!bs) return -1
    const n = as.localeCompare(bs)
    return (dir === 'asc' ? n : -n) || a.name.localeCompare(b.name)
  })
}

/**
 * Canonical form for `source_type`.
 *
 * The tenant records the same category several ways — 'APPLICATION' (12 rows)
 * alongside 'Application' (1), 'CLOUD' alongside 'Cloud Service'. Left alone
 * those split the column, the sort and any future grouping into separate
 * buckets that mean the same thing.
 *
 * Rationalised to ONE uppercase, hyphenated form:
 *   1. uppercase, which folds every pure casing variant automatically
 *      ('Application' -> 'APPLICATION', 'Unknown' -> 'UNKNOWN')
 *   2. spaces to hyphens, matching how the API already writes multi-word types
 *      ('Some Category' -> 'SOME-CATEGORY')
 *   3. an explicit merge list for genuine synonyms, below
 *
 * Anything not listed keeps its own identity — the default is to normalise the
 * spelling, never to merge two categories on a guess.
 */
const SOURCE_TYPE_MERGES: Record<string, string> = {
  // Same category, written as prose on one row and as a keyword on 15 others.
  'CLOUD SERVICE': 'CLOUD',
  // A specific management server, recorded with its product prefix.
  'NTLS(MANAGEMENT-SERVER)': 'MANAGEMENT-SERVER',
  // A generic label on a single row whose product really is a WAF.
  'SECURITY DEVICE': 'WAF',
}

/**
 * Canonical uppercase source type. Empty for nothing, so it still sorts last.
 */
export function sourceTypeLabel(raw: string | null | undefined): string {
  const trimmed = raw?.trim()
  if (!trimmed) return ''
  const upper = trimmed.toUpperCase()
  return SOURCE_TYPE_MERGES[upper] ?? upper.replace(/\s+/g, '-')
}

/** The synonym merges applied, for tests and for anyone auditing them. */
export const SOURCE_TYPE_MERGE_KEYS = Object.keys(SOURCE_TYPE_MERGES)

/**
 * Source cell: PRIMARY TYPE first, then the vendor/product name.
 *
 *     ZERO-TRUST · ACCOPS-HYSECURE
 *
 * The payload orders these the other way round (source_name, source_type), but
 * the type is the category you scan by and the name is the specific product
 * within it, so the type leads.
 *
 * The TYPE is rationalised to a canonical uppercase form (see
 * sourceTypeLabel); the NAME is passed through exactly as the API sends it,
 * because those are vendor and product identifiers.
 *
 * Returns EMPTY, not a dash, when there is nothing: the caller renders the
 * placeholder, and sortExtractors relies on empty sorting last.
 */
export function sourceLabel(e: Extractor): string {
  const type = sourceTypeLabel(e.source_type)
  const name = e.source_name?.trim()
  if (type && name) return `${type} · ${name}`
  return type || name || ''
}

/**
 * Detail cell. Blank when validation succeeded: the stage column already says
 * "Integrator Success", so repeating "Success" here is noise. Anything else is
 * worth reading, and a genuinely absent message still shows a dash so it stays
 * distinguishable from a success.
 */
export function validationDetailCell(e: Extractor): string {
  const d = parseValidationDetail(e.validation_msg)
  if (d.kind === 'text' && d.text.trim().toLowerCase() === 'success') return ''
  return summariseValidation(d)
}

/** Compact date for the Updated column. Empty when unknown. */
export function updatedDate(e: Extractor): string {
  const ms = updatedAtMs(e)
  return ms === null ? '' : new Date(ms).toISOString().slice(0, 10)
}

/* ------------------------------------------------------------------ finding */

/**
 * Free-text find over the columns a person scans by: extractor name, source
 * product and type, author. Case-insensitive substring; every whitespace-
 * separated term must hit somewhere (so "fortinet custom" narrows twice).
 */
export function matchesText(e: Extractor, text: string): boolean {
  const terms = text.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!terms.length) return true
  const hay = [e.name, e.source_name ?? '', sourceTypeLabel(e.source_type), e.author ?? '']
    .join(' ')
    .toLowerCase()
  return terms.every((t) => hay.includes(t))
}

/** Distinct canonical source types, alphabetical, blanks dropped. */
export function sourceTypeOptions(extractors: Extractor[]): string[] {
  return [...new Set(extractors.map((e) => sourceTypeLabel(e.source_type)).filter(Boolean))].sort()
}

/** Distinct authors, alphabetical, blanks dropped. */
export function authorOptions(extractors: Extractor[]): string[] {
  return [...new Set(extractors.map((e) => e.author?.trim() ?? '').filter(Boolean))].sort()
}

/* ------------------------------------------------------------------- pivots */

/**
 * Search pivot: the DQL that shows this extractor's recent events.
 *
 *     stream=FIREWALL,WEBFILTER where SourceName = 'FORTIGATE' | duration 24h | limit 100
 *
 * Streams come from the validation stream-mapping when the extractor carries
 * one, else `*` (cross-stream `stream=*` is CONFIRMED in docs/sql-dialect.md).
 * The filter assumes the list's `source_name` is the value events carry in
 * `$SourceName` — plausible (both come from the extractor YAML's
 * `source-name:`), but UNTESTED against a live tenant; tagged so in
 * docs/sql-dialect.md §7. Null when there is no source name to pivot on: the
 * caller then links to a blank search rather than to a query that cannot
 * narrow anything.
 */
export function searchPivotQuery(e: Extractor): string | null {
  const source = e.source_name?.trim()
  if (!source) return null
  const d = parseValidationDetail(e.validation_msg)
  const stream = d.kind === 'stream-mapping' && d.streams.length ? d.streams : '*'
  return buildDql({
    stream,
    time: { kind: 'relative', duration: '24h' },
    filters: [{ field: 'SourceName', op: '=', value: source }],
    limit: 100,
  })
}

/**
 * Sources pivot: `/fabric/sources?extractor=<name>`. Devices carry extractor
 * NAMES (`device.extractor[]`) alongside ids; the name is what the URL
 * carries because it is stable, readable and what the Sources table shows.
 */
export function sourcesPivotSearch(e: Extractor): { extractor: string } {
  return { extractor: e.name }
}

/* --------------------------------------------------------------- volumes */
/*
 * The health board (docs/extractors-requirements.md W1): two search
 * aggregations give every extractor's parsed volume and its fallout to the
 * OTHER stream over the window. Both ride the platform search engine — no
 * extractor endpoint carries volume (parsers/top stops at five).
 *
 * CONFIRMED live 2026-09-09 (gates E1/E2): `ExtractorID` arrives as a string,
 * the count as `count_col1`; `stream=*` took ~41 s on gotham (CB-31), OTHER
 * 3 s — hence the five-minute cache. Live failures degrade to "no volume
 * column", never to zeros.
 */

export interface ExtractorVolume {
  /** Events parsed by this extractor in the window (all streams). */
  parsed: number
  /** Events that only its fallback matched (`Stream: OTHER`). */
  fallout: number
}

/** Keyed by the NUMERIC extractor id as the search rows carry it ("4001"). */
export type VolumeMap = Record<string, ExtractorVolume>

export const VOLUME_WINDOW = '24h'
export const VOLUME_QUERY = `stream=* | groupby ExtractorID | select ExtractorID, count(ExtractorID) | duration ${VOLUME_WINDOW}`
export const FALLOUT_QUERY = `stream=OTHER | groupby ExtractorID | select ExtractorID, count(ExtractorID) | duration ${VOLUME_WINDOW}`

/** "4001" | "4001_gotham" | "4001_gotham_draft" → "4001": what `ExtractorID` holds on an event. */
export function volumeKey(e: Extractor): string {
  return e.extractor_id.split('_')[0] ?? e.extractor_id
}

/**
 * The count column comes back under a SYNTHETIC name (`count_col1`), never
 * the written one — read the first numeric column that is not the key.
 */
export function countsByExtractor(rows: Array<Record<string, unknown>>): Record<string, number> {
  const out: Record<string, number> = {}
  for (const row of rows) {
    const id = row.ExtractorID
    if (id === undefined || id === null || id === '—' || id === '') continue
    const n = Object.entries(row).find(([k, v]) => k !== 'ExtractorID' && typeof v === 'number')?.[1]
    if (typeof n !== 'number') continue
    const key = String(id)
    out[key] = (out[key] ?? 0) + n
  }
  return out
}

export function buildVolumeMap(parsed: Record<string, number>, fallout: Record<string, number>): VolumeMap {
  const map: VolumeMap = {}
  for (const [id, n] of Object.entries(parsed)) map[id] = { parsed: n, fallout: 0 }
  for (const [id, n] of Object.entries(fallout)) {
    const cur = map[id] ?? { parsed: 0, fallout: 0 }
    // Fallout rows are part of the stream=* total; parsed = the rest.
    map[id] = { parsed: Math.max(0, cur.parsed - n), fallout: n }
  }
  return map
}

export function volumeOf(e: Extractor, volumes: VolumeMap): ExtractorVolume {
  return volumes[volumeKey(e)] ?? { parsed: 0, fallout: 0 }
}

/* ---- the join, shared and remembered
 *
 * Two things measured live 2026-09-09 shaped this:
 *
 * 1. React StrictMode mounts, unmounts and remounts the page in dev. The
 *    first queryFn had consumed react-query's abort signal, so the unmount
 *    cancelled it (invoke → revoke) and the remount invoked again — FOUR
 *    engine jobs and two revokes for one board, two of them the 41 s scan.
 *    `sharedVolumeJob` keys the in-flight work per scope: a second caller
 *    joins the same promise, and the engine job is only revoked once every
 *    caller has been gone for `JOB_RELEASE_MS` (a remount re-attaches
 *    inside that window; a real navigation away still revokes).
 * 2. react-query's cache is in-memory, so a reload paid the 41 s again
 *    inside the five-minute window. The finished map is also written to
 *    localStorage (live only) and seeds the query as `initialData`, so a
 *    reload within `VOLUME_STALE_MS` shows the board at once.
 */

/** How long a finished join is trusted — react-query staleTime and the persisted copy alike. */
export const VOLUME_STALE_MS = 300_000
/** Grace period before an abandoned job is revoked (StrictMode remounts re-attach well inside it). */
export const JOB_RELEASE_MS = 1_000

const volumeCacheKey = (clusterId: string, scopeId: string) =>
  `bloo.extractor-volumes.${clusterId}.${scopeId}.${VOLUME_WINDOW}`

/** The persisted join, or null when absent, malformed, or older than `VOLUME_STALE_MS`. */
export function readVolumeCache(clusterId: string, scopeId: string, now = Date.now()): { map: VolumeMap; at: number } | null {
  try {
    const raw = globalThis.localStorage?.getItem(volumeCacheKey(clusterId, scopeId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { at?: unknown; map?: unknown }
    if (typeof parsed.at !== 'number' || typeof parsed.map !== 'object' || parsed.map === null) return null
    if (now - parsed.at > VOLUME_STALE_MS) return null
    return { map: parsed.map as VolumeMap, at: parsed.at }
  } catch {
    return null
  }
}

export function writeVolumeCache(clusterId: string, scopeId: string, map: VolumeMap, at = Date.now()): void {
  try {
    globalThis.localStorage?.setItem(volumeCacheKey(clusterId, scopeId), JSON.stringify({ at, map }))
  } catch {
    // Quota or private mode — the in-memory query still has the answer.
  }
}

interface VolumeJob {
  promise: Promise<VolumeMap>
  ctrl: AbortController
  refs: number
  release: ReturnType<typeof setTimeout> | null
}

const volumeJobs = new Map<string, VolumeJob>()

/**
 * One engine job per `key` at a time. `run` receives the job's own signal;
 * `signal` is the caller's (react-query's) and only detaches the caller.
 * Exported for the test; callers go through `useExtractorVolumes`.
 */
export function sharedVolumeJob(
  key: string,
  signal: AbortSignal,
  run: (signal: AbortSignal) => Promise<VolumeMap>,
): Promise<VolumeMap> {
  let job = volumeJobs.get(key)
  if (!job) {
    const ctrl = new AbortController()
    const fresh: VolumeJob = { ctrl, refs: 0, release: null, promise: Promise.resolve({}) }
    fresh.promise = run(ctrl.signal).finally(() => {
      if (volumeJobs.get(key) === fresh) volumeJobs.delete(key)
    })
    // A job nobody is left awaiting must not surface as an unhandled rejection.
    fresh.promise.catch(() => {})
    volumeJobs.set(key, fresh)
    job = fresh
  }
  const j = job
  if (j.release) {
    clearTimeout(j.release)
    j.release = null
  }
  j.refs++
  const detach = () => {
    j.refs--
    if (j.refs > 0) return
    j.release = setTimeout(() => {
      if (j.refs > 0) return
      j.ctrl.abort()
      if (volumeJobs.get(key) === j) volumeJobs.delete(key)
    }, JOB_RELEASE_MS)
  }
  if (signal.aborted) detach()
  else signal.addEventListener('abort', detach, { once: true })
  return j.promise
}

/** Test seam: forget every in-flight job. */
export function resetVolumeJobs(): void {
  for (const j of volumeJobs.values()) {
    if (j.release) clearTimeout(j.release)
    j.ctrl.abort()
  }
  volumeJobs.clear()
}

/**
 * Two aggregations, run in parallel through the platform engine. One
 * failure fails the whole thing — a half-joined board would read as truth.
 *
 * `enabled: false` keeps the ~41 s scan from starting (Datafabric · Overview
 * runs it only on a click) while still answering from a warm cache — the
 * query client's, or the live localStorage copy — so a scan the Extractors
 * page already ran shows without a second one.
 */
export function useExtractorVolumes({ clusterId, scopeId, enabled = true }: { clusterId: string; scopeId: string; enabled?: boolean }) {
  const isDemo = isDemoSession()
  // Read once per mount: initialData is only consulted when the query is created.
  const [cached] = useState(() => (isDemo ? null : readVolumeCache(clusterId, scopeId)))
  return useQuery<VolumeMap>({
    queryKey: ['extractor-volumes', clusterId, scopeId, VOLUME_WINDOW, isDemo ? 'demo' : 'live'],
    queryFn: ({ signal }) =>
      sharedVolumeJob(`${clusterId}/${scopeId}/${isDemo ? 'demo' : 'live'}`, signal, async (jobSignal) => {
        const api = searchApiFor(isDemo)
        const scope = { clusterId, scopeId }
        const [all, other] = await Promise.all([
          runToCompletion(api, scope, VOLUME_QUERY, undefined, jobSignal, 2000),
          runToCompletion(api, scope, FALLOUT_QUERY, undefined, jobSignal, 2000),
        ])
        const map = buildVolumeMap(countsByExtractor(all.page.rows), countsByExtractor(other.page.rows))
        if (!isDemo) writeVolumeCache(clusterId, scopeId, map)
        return map
      }),
    ...(cached ? { initialData: cached.map, initialDataUpdatedAt: cached.at } : {}),
    staleTime: VOLUME_STALE_MS,
    retry: false,
    enabled,
  })
}
